export const APP_VERSION_PATH = '/app-version.json'
export const APP_VERSION_CHECK_TTL_MS = 60_000
export const APP_VERSION_UNKNOWN_WARN_THRESHOLD = 3

export type AppVersionState = 'current' | 'stale' | 'unknown'

export interface AppVersionInfo {
  buildId: string
  commit?: string | null
  releasedAt?: string | null
}

export interface AppVersionStatus {
  state: AppVersionState
  currentBuildId: string
  latestBuildId: string | null
}

export interface AppVersionCheckTiming {
  now: number
  lastCheckedAt: number | null
  ttlMs?: number
  force?: boolean
}

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

export function normalizeBuildId(value: string | null | undefined): string | null {
  const trimmed = value?.trim()
  return trimmed ? trimmed : null
}

export function resolveCurrentBuildId(value: string | null | undefined, isDevLike: boolean): string | null {
  if (isDevLike) return null
  return normalizeBuildId(value)
}

export function parseAppVersionInfo(value: unknown): AppVersionInfo | null {
  if (!value || typeof value !== 'object') return null

  const record = value as Record<string, unknown>
  const buildId = typeof record.buildId === 'string' ? normalizeBuildId(record.buildId) : null
  if (!buildId) return null

  return {
    buildId,
    commit: typeof record.commit === 'string' ? record.commit : null,
    releasedAt: typeof record.releasedAt === 'string' ? record.releasedAt : null,
  }
}

export function computeAppVersionStatus(
  currentBuildId: string | null,
  latest: AppVersionInfo | null,
): AppVersionStatus {
  if (!currentBuildId || !latest) {
    return {
      state: 'unknown',
      currentBuildId: currentBuildId ?? '',
      latestBuildId: latest?.buildId ?? null,
    }
  }

  if (currentBuildId === latest.buildId) {
    return {
      state: 'current',
      currentBuildId,
      latestBuildId: latest.buildId,
    }
  }

  return {
    state: 'stale',
    currentBuildId,
    latestBuildId: latest.buildId,
  }
}

export function shouldCheckAppVersion({
  now,
  lastCheckedAt,
  ttlMs = APP_VERSION_CHECK_TTL_MS,
  force = false,
}: AppVersionCheckTiming): boolean {
  if (force) return true
  if (lastCheckedAt === null) return true
  return now - lastCheckedAt >= ttlMs
}

export function isMutatingMethod(method: string | null | undefined): boolean {
  return MUTATING_METHODS.has((method ?? 'GET').toUpperCase())
}

export function shouldWarnForConsecutiveUnknown(count: number): boolean {
  return count >= APP_VERSION_UNKNOWN_WARN_THRESHOLD
}
